import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, PencilSimple, Trash, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * CustomerGroupsPage — Masters → Customer Groups.
 * Tiny CRUD over `/api/customer-groups`. Each row shows the group name and
 * how many customers are currently assigned to it. Delete is blocked while
 * any customer references the group (mirrors the Services + Account
 * delete-guard pattern).
 */
export default function CustomerGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const refresh = () => {
    setLoading(true);
    api.get('/customer-groups').then(r => setGroups(r.data || [])).catch(() => setGroups([])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const add = async () => {
    const n = newName.trim();
    if (!n) { toast.error('Group name required'); return; }
    try {
      await api.post('/customer-groups', { name: n });
      toast.success(`Group "${n}" created`);
      setNewName('');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async (id) => {
    const n = editName.trim();
    if (!n) { toast.error('Group name required'); return; }
    try {
      await api.put(`/customer-groups/${id}`, { name: n });
      toast.success('Group renamed');
      setEditId(null);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/customer-groups/${g.id}`);
      toast.success('Group deleted');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-xl border-[#E4E4E7]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-base font-semibold text-[#0A0A0A]">Customer Groups</h1>
              <p className="text-xs text-[#A1A1AA] mt-1">Tag customers (Wholesale, Walk-in, Corporate, …) to filter and report by group.</p>
            </div>
            <span className="text-xs text-[#A1A1AA]">{groups.length} group{groups.length === 1 ? '' : 's'}</span>
          </div>

          {/* Inline add form */}
          <div className="flex items-center gap-2 mb-4">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="New group name (e.g. Wholesale)"
              className="rounded-lg h-9 text-sm max-w-sm"
              onKeyDown={e => { if (e.key === 'Enter') add(); }}
              data-testid="group-new-input"
            />
            <Button onClick={add} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg h-9 text-xs" data-testid="group-add-btn">
              <Plus size={14} className="mr-1" /> Add Group
            </Button>
          </div>

          {loading ? (
            <p className="text-sm text-[#A1A1AA] py-6 text-center">Loading…</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-[#A1A1AA] italic py-6 text-center">No groups yet — add one to get started.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right">Customers</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em] font-semibold text-right w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.id} className="border-[#F4F4F5]" data-testid={`group-row-${g.id}`}>
                    <TableCell className="text-sm font-medium">
                      {editId === g.id ? (
                        <Input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveEdit(g.id); if (e.key === 'Escape') setEditId(null); }}
                          className="rounded-sm h-8 text-sm max-w-xs"
                          data-testid={`group-edit-input-${g.id}`}
                        />
                      ) : g.name}
                    </TableCell>
                    <TableCell className="text-right text-sm font-mono text-[#52525B]">{g.customer_count || 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {editId === g.id ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => saveEdit(g.id)} className="h-7 w-7 p-0 text-[#10B981]" data-testid={`group-save-btn-${g.id}`}><Check size={14} /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-7 w-7 p-0"><X size={14} /></Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => { setEditId(g.id); setEditName(g.name); }} className="h-7 w-7 p-0" data-testid={`group-edit-btn-${g.id}`}><PencilSimple size={14} /></Button>
                            {!g.in_use && (
                              <Button size="sm" variant="ghost" onClick={() => del(g)} className="h-7 w-7 p-0 text-[#EF4444]" data-testid={`group-delete-btn-${g.id}`}><Trash size={14} /></Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
